import {
  ABSENCE_REASONS,
  type AbsenceReason,
  type AbsenceRequest,
  type ViewContext,
  addDays,
  shortDate,
  todayIso,
  useAbsenceRequests,
  useCurrentContext,
  useRequestActions,
} from '@shkola/api';
import { HouseIcon } from '@phosphor-icons/react/House';
import { MoonIcon } from '@phosphor-icons/react/Moon';
import { StethoscopeIcon } from '@phosphor-icons/react/Stethoscope';
import { ThermometerIcon } from '@phosphor-icons/react/Thermometer';
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Field, Modal, SecondaryButton, Select, Spinner } from '../components/ui';
import {
  Block,
  GRAD,
  type Grad,
  INK,
  INK_FAINT,
  INK_SOFT,
  PageHead,
  PillIcon,
  TaskArt,
  Tabs,
  grad,
  pillClass,
} from '../design/forms';
import { ChatCircleIcon, type FilledIcon, PlusIcon } from '../design/icons';
import { buttonClass } from '../design/ui';

/*
 * «Заяви про відсутність» in «Форми 2» (28.10): the title on the paper with the one action beside it (a
 * new statement for the family, the choice «на розгляді / усі» for the staff), then one white block of
 * statements — each a row with its reason as a filled icon in the reason's colour, who and when, the
 * words, the answer, the status as a soft label and the row's own button.
 */

const period = (r: { fromDate: string; toDate: string }) =>
  r.fromDate === r.toDate
    ? shortDate(r.fromDate)
    : `${shortDate(r.fromDate)} – ${shortDate(r.toDate)}`;

/** A reason as a picture: its icon and its colour. */
const REASON_LOOK: Record<AbsenceReason, { icon: FilledIcon; tone: Grad }> = {
  illness: { icon: ThermometerIcon, tone: 'coral' },
  medical: { icon: StethoscopeIcon, tone: 'sky' },
  family: { icon: HouseIcon, tone: 'butter' },
  alarm_night: { icon: MoonIcon, tone: 'lavender' },
  other: { icon: ChatCircleIcon, tone: 'stone' },
};

const STATUS_TONE: Record<AbsenceRequest['status'], Grad> = {
  submitted: 'lavender',
  accepted: 'mint',
  rejected: 'coral',
};

const TEXTAREA =
  'w-full rounded-[14px] bg-white px-3.5 py-2.5 text-[14px] text-ink outline-none ring-1 ring-[#E4E0D7] ring-inset transition-shadow placeholder:text-faint focus:ring-2 focus:ring-[var(--color-primary)]';

/** The words over a field, as `Field` has them. */
function Caption({ children }: { children: ReactNode }) {
  return (
    <span className="mb-1.5 block text-[13px] font-medium" style={{ color: INK_SOFT }}>
      {children}
    </span>
  );
}

function NewRequest({
  ctx,
  onClose,
}: {
  ctx: Extract<ViewContext, { kind: 'guardian' }>;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const submit = useRequestActions().submit;
  const [from, setFrom] = useState(todayIso());
  const [to, setTo] = useState(todayIso());
  const [reason, setReason] = useState<AbsenceReason>('illness');
  const [comment, setComment] = useState('');
  // made once per form: sending twice (a lost answer, a double tap) stays one statement
  const [clientId] = useState(() => crypto.randomUUID());
  return (
    <Modal title={t('requests.new', { name: ctx.child.firstName })} onClose={onClose}>
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          submit.mutate({
            schoolId: ctx.schoolId,
            studentId: ctx.child.memberId,
            authorId: ctx.memberId,
            clientId,
            fromDate: from,
            toDate: to < from ? from : to,
            reason,
            comment,
          });
          onClose();
        }}
      >
        <div className="grid grid-cols-2 gap-3">
          <Field
            id="from"
            type="date"
            label={t('requests.from')}
            value={from}
            min={addDays(todayIso(), -14)}
            onChange={(e) => setFrom(e.target.value)}
            required
          />
          <Field
            id="to"
            type="date"
            label={t('requests.to')}
            value={to < from ? from : to}
            min={from}
            max={addDays(from, 30)}
            onChange={(e) => setTo(e.target.value)}
            required
          />
        </div>
        <label className="block">
          <Caption>{t('requests.reason')}</Caption>
          <Select
            className="h-11 w-full"
            value={reason}
            onChange={(e) => setReason(e.target.value as AbsenceReason)}
          >
            {ABSENCE_REASONS.map((r) => (
              <option key={r} value={r}>
                {t(`requests.reasons.${r}`)}
              </option>
            ))}
          </Select>
        </label>
        <label className="block">
          <Caption>{t('requests.comment')}</Caption>
          <textarea
            className={TEXTAREA}
            rows={3}
            maxLength={1000}
            placeholder={t('requests.commentHint')}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
          />
        </label>
        <p className="text-[12.5px] leading-[18px]" style={{ color: INK_SOFT }}>
          {t('requests.privacy')}
        </p>
        <Button type="submit" disabled={submit.isPending}>
          {t('requests.send')}
        </Button>
      </form>
    </Modal>
  );
}

function Review({ request, onClose }: { request: AbsenceRequest; onClose: () => void }) {
  const { t } = useTranslation();
  const review = useRequestActions().review;
  const [comment, setComment] = useState('');
  const decide = (status: 'accepted' | 'rejected') =>
    void review.mutateAsync({ id: request.id, status, comment }).then(onClose);
  const look = REASON_LOOK[request.reason];
  return (
    <Modal title={`${request.studentName} · ${period(request)}`} onClose={onClose}>
      <div
        className="mb-4 flex items-start gap-3 rounded-[20px] p-4"
        style={{ backgroundImage: grad(look.tone), color: GRAD[look.tone].ink }}
      >
        <look.icon size={22} weight="fill" aria-hidden className="mt-0.5 shrink-0" />
        <dl className="min-w-0 space-y-2 text-[14px] leading-5">
          <div>
            <dt className="text-[12.5px]">{t('requests.reason')}</dt>
            <dd className="font-medium" style={{ color: INK }}>
              {t(`requests.reasons.${request.reason}`)}
            </dd>
          </div>
          {request.comment && (
            <div>
              <dt className="text-[12.5px]">{t('requests.comment')}</dt>
              <dd className="whitespace-pre-line" style={{ color: INK }}>
                {request.comment}
              </dd>
            </div>
          )}
          {request.authorName && (
            <div>
              <dt className="text-[12.5px]">{t('requests.author')}</dt>
              <dd style={{ color: INK }}>{request.authorName}</dd>
            </div>
          )}
        </dl>
      </div>
      <Field
        id="review"
        label={t('requests.reply')}
        maxLength={500}
        value={comment}
        onChange={(e) => setComment(e.target.value)}
      />
      <div className="mt-4 flex gap-2">
        <Button
          className="!w-auto px-6"
          disabled={review.isPending}
          onClick={() => decide('accepted')}
        >
          {t('requests.accept')}
        </Button>
        <SecondaryButton
          className="h-11 px-6"
          disabled={review.isPending}
          onClick={() => decide('rejected')}
        >
          {t('requests.reject')}
        </SecondaryButton>
      </div>
    </Modal>
  );
}

/** A status: a soft label in its colour. */
function Status({ status }: { status: AbsenceRequest['status'] }) {
  const { t } = useTranslation();
  const tone = STATUS_TONE[status];
  return (
    <span
      className="inline-flex h-6 shrink-0 items-center rounded-[7px] px-2 text-[12px] leading-none font-medium whitespace-nowrap"
      style={{ backgroundImage: grad(tone), color: GRAD[tone].ink }}
    >
      {t(`requests.status.${status}`)}
    </span>
  );
}

function RequestRow({
  request,
  showStudent,
  action,
}: {
  request: AbsenceRequest;
  showStudent: boolean;
  action?: ReactNode;
}) {
  const { t } = useTranslation();
  const look = REASON_LOOK[request.reason];
  return (
    <li className="flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-[#E4E0D7] py-4 first:border-t-0 first:pt-1 last:pb-1">
      <span
        aria-hidden
        className="flex size-11 shrink-0 items-center justify-center rounded-full"
        style={{ backgroundImage: grad(look.tone), color: GRAD[look.tone].ink }}
      >
        <look.icon size={20} weight="fill" />
      </span>
      <div className="min-w-0 flex-1 basis-[220px]">
        <p className="text-[16px] leading-[22px] font-medium" style={{ color: INK }}>
          {showStudent && (
            <>
              {request.studentName}
              {request.classLabel && (
                <span className="ml-1.5 font-normal" style={{ color: INK_FAINT }}>
                  {request.classLabel}
                </span>
              )}
              <span style={{ color: INK_FAINT }}>{' · '}</span>
            </>
          )}
          {period(request)}
        </p>
        <p className="text-[13.5px] leading-5" style={{ color: INK_SOFT }}>
          {t(`requests.reasons.${request.reason}`)}
          {request.comment ? ` · ${request.comment}` : ''}
        </p>
        {request.reviewComment && (
          <p className="mt-1 text-[13.5px] leading-5" style={{ color: INK }}>
            <span style={{ color: INK_SOFT }}>{t('requests.replyShort')}:</span>{' '}
            {request.reviewComment}
          </p>
        )}
      </div>
      <span className="ml-auto flex shrink-0 items-center gap-3">
        <Status status={request.status} />
        {action}
      </span>
    </li>
  );
}

/** Nothing to show: a quiet block with its picture. */
function Empty({ children }: { children: ReactNode }) {
  return (
    <div className="relative isolate flex min-h-[120px] items-center overflow-hidden">
      <TaskArt
        kind="done"
        tone="lime"
        size={120}
        ghost
        className="pointer-events-none absolute top-1/2 right-4 -z-10 -translate-y-1/2"
      />
      <p className="text-[16px]" style={{ color: INK_SOFT }}>
        {children}
      </p>
    </div>
  );
}

function FamilyRequests({ ctx }: { ctx: Extract<ViewContext, { kind: 'guardian' | 'student' }> }) {
  const { t } = useTranslation();
  const requests = useAbsenceRequests({ studentId: ctx.child.memberId });
  const withdraw = useRequestActions().withdraw;
  const [writing, setWriting] = useState(false);
  const list = requests.data ?? [];
  return (
    <div className="flex flex-col gap-4">
      <PageHead title={t('requests.title')} note={ctx.child.firstName}>
        {ctx.kind === 'guardian' && (
          <button type="button" className={pillClass('dark')} onClick={() => setWriting(true)}>
            {t('requests.newShort')}
            <PillIcon icon={PlusIcon} />
          </button>
        )}
      </PageHead>
      <Block>
        {requests.isPending ? (
          <Spinner />
        ) : list.length === 0 ? (
          <Empty>{t('requests.empty')}</Empty>
        ) : (
          <ul className="flex flex-col">
            {list.map((r) => (
              <RequestRow
                key={r.id}
                request={r}
                showStudent={false}
                action={
                  ctx.kind === 'guardian' &&
                  r.status === 'submitted' && (
                    <button
                      type="button"
                      className={buttonClass('outline', 'sm')}
                      onClick={() => withdraw.mutate(r.id)}
                    >
                      {t('requests.withdraw')}
                    </button>
                  )
                }
              />
            ))}
          </ul>
        )}
      </Block>
      {writing && ctx.kind === 'guardian' && (
        <NewRequest ctx={ctx} onClose={() => setWriting(false)} />
      )}
    </div>
  );
}

function StaffRequests({ ctx }: { ctx: Extract<ViewContext, { kind: 'teacher' | 'admin' }> }) {
  const { t } = useTranslation();
  const requests = useAbsenceRequests({ schoolId: ctx.schoolId });
  const [show, setShow] = useState<'submitted' | 'all'>('submitted');
  const [reviewing, setReviewing] = useState<AbsenceRequest>();
  const pending = (requests.data ?? []).filter((r) => r.status === 'submitted');
  const list = show === 'submitted' ? pending : (requests.data ?? []);
  return (
    <div className="flex flex-col gap-4">
      <PageHead title={t('requests.title')}>
        <Tabs<'submitted' | 'all'>
          value={show}
          label={t('requests.title')}
          onChange={setShow}
          options={[
            { id: 'submitted', label: t('requests.pending', { count: pending.length }) },
            { id: 'all', label: t('requests.all') },
          ]}
        />
      </PageHead>
      <Block>
        {requests.isPending ? (
          <Spinner />
        ) : list.length === 0 ? (
          <Empty>{show === 'submitted' ? t('requests.nothingPending') : t('requests.empty')}</Empty>
        ) : (
          <ul className="flex flex-col">
            {list.map((r) => (
              <RequestRow
                key={r.id}
                request={r}
                showStudent
                action={
                  r.status === 'submitted' && (
                    <button
                      type="button"
                      className={buttonClass('primary', 'sm')}
                      onClick={() => setReviewing(r)}
                    >
                      {t('requests.decide')}
                    </button>
                  )
                }
              />
            ))}
          </ul>
        )}
      </Block>
      {reviewing && <Review request={reviewing} onClose={() => setReviewing(undefined)} />}
    </div>
  );
}

/** Statements about absence: families write them, the homeroom teacher decides. */
export function RequestsPage() {
  const ctx = useCurrentContext();
  if (ctx.kind === 'psychologist') return null;
  return ctx.kind === 'guardian' || ctx.kind === 'student' ? (
    <FamilyRequests key={ctx.key} ctx={ctx} />
  ) : (
    <StaffRequests key={ctx.key} ctx={ctx} />
  );
}
